import { SelectInputWithLabelT } from '@components/tailwind-components/Input/SelectInputWithLabelT';
import type { DataSourceFolderWithSubfoldersDTO } from '@conversed/rest/src/contracts/data-sources-folders/data-sources-folders.schema';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpiryDatePickerSection } from './ExpiryDatePickerSection';

/** Sentinel used to represent "no subfolder selected" in the dropdown. */
const SUBFOLDER_NONE = '__none__';

interface FolderSelectionSectionProps {
  folders: DataSourceFolderWithSubfoldersDTO[];
  selectedFolderId: string;
  selectedSubfolderId: string;
  onFolderChange: (folderId: string) => void;
  onSubfolderChange: (subfolderId: string) => void;
  // Expiry date (optional – shown when provided; auto-reset on folder/subfolder change)
  expiryDate?: Date | null;
  onExpiryDateChange?: (date: Date | null) => void;
  isTargetTemporary?: boolean;
}

export const FolderSelectionSection: React.FC<FolderSelectionSectionProps> = ({
  folders,
  selectedFolderId,
  selectedSubfolderId,
  onFolderChange,
  onSubfolderChange,
  expiryDate,
  onExpiryDateChange,
  isTargetTemporary,
}) => {
  const { t } = useTranslation();

  const selectedFolder = folders.find(
    (folder) => folder._id?.toString() === selectedFolderId,
  );

  const folderOptions = useMemo(() => {
    const visible = folders.filter((f) => f.metadata?.canCreateSubfolders);
    return visible.map((folder) => {
      const folderId = folder._id?.toString() ?? '';
      return {
        value: folderId,
        description: t(folder.name ?? ''),
      };
    });
  }, [folders, t]);

  const subfolderValues = useMemo(
    () => [
      {
        value: SUBFOLDER_NONE,
        description: t('data-sources.sidebar.subfolder-none'),
      },
      ...(selectedFolder?.subfolders?.map((subfolder) => ({
        value: subfolder._id?.toString() ?? '',
        description: subfolder.name,
      })) ?? []),
    ],
    [selectedFolder?.subfolders, t],
  );

  const handleFolderChange = (folderId: string) => {
    onFolderChange(folderId);
    // Reset subfolder and expiry on folder change
    onSubfolderChange('');
    onExpiryDateChange?.(null);
  };

  const handleSubfolderChange = (value: string) => {
    onSubfolderChange(value === SUBFOLDER_NONE ? '' : value);
    onExpiryDateChange?.(null);
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col'>
        <SelectInputWithLabelT
          values={folderOptions}
          value={selectedFolderId}
          className='text-gray-600'
          labelClassName='font-medium'
          onChange={(e) => handleFolderChange(e.target.value)}
          label={t('data-sources.sidebar.file-details.folder')}
          noOptionSelectedText={t('data-sources.sidebar.folder-placeholder')}
          selectInputParentClassName='!mb-0'
        />
      </div>

      {/* Subfolder dropdown – only shown when a folder is selected and has subfolders */}
      {selectedFolderId &&
        selectedFolder?.subfolders &&
        selectedFolder.subfolders.length > 0 && (
          <div className='flex flex-col'>
            <SelectInputWithLabelT
              values={subfolderValues}
              value={selectedSubfolderId}
              label={t('data-sources.sidebar.subfolder')}
              className='text-gray-600'
              labelClassName='font-medium'
              onChange={(e) => handleSubfolderChange(e.target.value)}
              noOptionSelectedText={t(
                'data-sources.sidebar.subfolder-placeholder',
              )}
              selectInputParentClassName='!mb-0'
            />
          </div>
        )}

      {/* Expiry date – only rendered when the callback is provided */}
      {onExpiryDateChange !== undefined && (
        <ExpiryDatePickerSection
          id='folder-expiry-date'
          expiryDate={expiryDate ?? null}
          onChange={onExpiryDateChange}
          isTargetTemporary={isTargetTemporary ?? false}
        />
      )}
    </div>
  );
};
